import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { guidFor } from '@ember/object/internals';
import { fn, hash } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { eq } from 'tio-ui/utilities';
import { PhCaretDown, PhCheck, PhMagnifyingGlass, PhX, PhSpinner } from 'ember-phosphor-icons';
import {
  computeDropdownPosition,
  installOutsideClickHandler,
  installScrollHandler,
} from '../utils/floating.ts';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// Types & Option shape
// ---------------------------------------------------------------------------

/**
 * A single option in the select dropdown.
 * Options may be simple strings via @items, or objects conforming to this shape.
 */
export interface SelectOption {
  /** Unique key for the option */
  key: string;
  /** Display label */
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Optional icon component */
  icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** Optional group label for grouped options */
  group?: string;
}

type SelectSize = 'sm' | 'md' | 'lg';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalise a raw items array → SelectOption[].
 * Accepts string[], { key, label }[], or SelectOption[].
 */
function normalizeOptions(items: readonly unknown[]): SelectOption[] {
  if (!items?.length) return [];
  return items.map((item, idx) => {
    if (typeof item === 'string') {
      return { key: item, label: item };
    }
    const obj = item as Record<string, unknown>;
    return {
      key: String(obj['key'] ?? obj['id'] ?? obj['value'] ?? idx),
      label: String(obj['label'] ?? obj['name'] ?? obj['text'] ?? obj['value'] ?? obj['key'] ?? ''),
      description: obj['description'] as string | undefined,
      icon: obj['icon'] as SelectOption['icon'],
      disabled: Boolean(obj['disabled']),
      group: obj['group'] as string | undefined,
    };
  });
}

function groupOptions(
  options: SelectOption[]
): { group: string | null; options: SelectOption[] }[] {
  const groups: Map<string | null, SelectOption[]> = new Map();
  for (const opt of options) {
    const g = opt.group ?? null;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(opt);
  }
  return [...groups.entries()].map(([group, opts]) => ({ group, options: opts }));
}

// ---------------------------------------------------------------------------
// Main Select component
// ---------------------------------------------------------------------------

/**
 * Reimagined Select
 *
 * A zero-dependency select component replacing Frontile's Select and RemoteSelect.
 * Renders its dropdown via `{{in-element}}` to document.body, avoiding z-index
 * and stacking-context issues inside drawers, modals, and sticky navs.
 *
 * Supports four modes:
 * 1. **Basic select** – single pick from a static list
 * 2. **Multi-select** – pick multiple items, shown as chips
 * 3. **Searchable select** – type-ahead filter with local data
 * 4. **Remote search** – async search with debounce, single or multi
 *
 * @example Basic single select
 * ```gts
 * <Select
 *   @label="Status"
 *   @items={{array "Active" "Inactive" "Pending"}}
 *   @selected={{this.status}}
 *   @onChange={{this.setStatus}}
 * />
 * ```
 *
 * @example Multi-select with search
 * ```gts
 * <Select
 *   @label="Tags"
 *   @items={{this.tagOptions}}
 *   @multiple={{true}}
 *   @searchable={{true}}
 *   @selectedKeys={{this.selectedTagKeys}}
 *   @onChangeKeys={{this.setTagKeys}}
 *   @placeholder="Search tags…"
 * />
 * ```
 *
 * @example Remote search with multi-select
 * ```gts
 * <Select
 *   @label="Employees"
 *   @items={{this.searchResults}}
 *   @multiple={{true}}
 *   @searchable={{true}}
 *   @isLoading={{this.searchTask.isRunning}}
 *   @onSearch={{this.searchEmployees}}
 *   @selectedKeys={{this.selectedEmployeeIds}}
 *   @onChangeKeys={{this.setSelectedIds}}
 *   @placeholder="Search by name or email…"
 * >
 *   <:option as |opt|>
 *     <div class="flex flex-col">
 *       <span class="font-medium">{{opt.label}}</span>
 *       <span class="text-xs text-gray-500">{{opt.description}}</span>
 *     </div>
 *   </:option>
 * </Select>
 * ```
 */
interface SelectSignature {
  Args: {
    /** Array of options — strings, or objects with key/label/description/icon/disabled/group */
    items: readonly unknown[];
    /** Label displayed above the trigger */
    label?: string;
    /** Placeholder text when nothing is selected */
    placeholder?: string;

    // --- Single-select mode ---
    /** Currently selected key (single mode) */
    selected?: string | null;
    /** Called with the selected key (single mode) */
    onChange?: (key: string) => void;

    // --- Multi-select mode ---
    /** Enable multi-select */
    multiple?: boolean;
    /** Currently selected keys (multi mode) */
    selectedKeys?: string[];
    /** Called with all selected keys (multi mode) */
    onChangeKeys?: (keys: string[]) => void;

    // --- Search ---
    /** Enable search/filter input in the dropdown */
    searchable?: boolean;
    /** Called on search input change — for remote search. Receives the query string */
    onSearch?: (query: string) => void;
    /** Debounce delay for @onSearch in ms @default 300 */
    searchDebounce?: number;
    /** Show a loading spinner (e.g. while remote results load) */
    isLoading?: boolean;
    /** Text shown when no results match @default "No results found" */
    noResultsText?: string;

    // --- Appearance ---
    /** Size variant @default "md" */
    size?: SelectSize;
    /** Allow clearing the selection @default true */
    clearable?: boolean;
    /** Validation error messages (renders red border + text) */
    errors?: string[];
    /** Disable the select */
    disabled?: boolean;
    /** Hint text below the input */
    hint?: string;
    /** Max height of the dropdown in px @default 280 */
    maxHeight?: number;
    // --- Form integration ---
    /**
     * HTML name attribute. When set, a hidden <select> is rendered so the
     * selected value is included in native FormData (works with @frontile/forms Form).
     */
    name?: string;
    /**
     * Override the auto-generated trigger element id.
     * Useful for associating an external `<label for=...>` with the trigger button.
     */
    id?: string;
  };
  Blocks: {
    /** Custom option rendering — receives the SelectOption */
    option?: [SelectOption];
    /** Custom trigger rendering — receives { selectedOptions, isOpen, placeholder } */
    trigger?: [{ selectedOptions: SelectOption[]; isOpen: boolean; placeholder: string }];
    /** Optional content above the options list (e.g. "Create new" button) */
    header?: [];
    /** Optional content below the options list */
    footer?: [];
    /** Content rendered at the start (left) of the trigger, before the selected text. Typically an icon. */
    startContent?: [];
    /** Content rendered at the end (right) of the trigger, before the caret. Typically an icon or badge. */
    endContent?: [];
  };
  Element: HTMLDivElement;
}

export default class Select extends Component<SelectSignature> {
  // ---- Internal state --------------------------------------------------------
  @tracked private _isOpen = false;
  @tracked private _searchQuery = '';
  @tracked private _highlightedIndex = -1;
  @tracked private _triggerRect: DOMRect | null = null;

  private _searchTimer: ReturnType<typeof setTimeout> | undefined;
  private _listboxElement: HTMLElement | null = null;
  private _triggerElement: HTMLElement | null = null;
  private _searchInputElement: HTMLInputElement | null = null;

  /**
   * Cache of selected options keyed by option key.
   * Ensures remote-search Selects keep displaying selected labels even
   * when @items no longer contains the previously selected option.
   */
  private _selectedCache = new Map<string, SelectOption>();

  /** Per-instance unique ID for ARIA references */
  private _instanceId = `select-${guidFor(this)}`;

  get triggerId() {
    return this.args.id ?? `${this._instanceId}-trigger`;
  }

  get listboxId() {
    return `${this._instanceId}-listbox`;
  }

  get labelId() {
    return `${this._instanceId}-label`;
  }

  optionId = (key: string) => {
    return `${this._instanceId}-option-${key}`;
  };

  /**
   * The ID of the currently highlighted option for `aria-activedescendant`.
   * Returns undefined when nothing is highlighted so the attribute is omitted.
   */
  private get activeDescendantId(): string | undefined {
    const opts = this.flatFilteredOptions;
    const opt = this._highlightedIndex >= 0 ? opts[this._highlightedIndex] : undefined;
    return opt ? this.optionId(opt.key) : undefined;
  }

  // ---- Derived state ---------------------------------------------------------

  private get size(): SelectSize {
    return this.args.size ?? 'md';
  }
  private get clearable() {
    return this.args.clearable ?? true;
  }
  private get maxHeight() {
    return this.args.maxHeight ?? 280;
  }
  private get searchDebounce() {
    return this.args.searchDebounce ?? 300;
  }
  private get noResultsText() {
    return this.args.noResultsText ?? 'No results found';
  }
  private get multiple() {
    return this.args.multiple ?? false;
  }
  private get searchable() {
    return this.args.searchable ?? false;
  }
  private get hasRemoteSearch() {
    return typeof this.args.onSearch === 'function';
  }
  private get hasErrors() {
    return this.args.errors && this.args.errors.length > 0;
  }

  /**
   * The current value for the hidden form input.
   * Single-select: the selected key. Multi-select: comma-joined keys.
   */
  private get formValue(): string {
    if (this.multiple) {
      return [...this.selectedKeysSet].join(',');
    }
    return this.args.selected ?? '';
  }

  /** Portal destination for the dropdown — always document.body (use document.querySelector in tests) */
  get portalDestination(): Element {
    return document.body;
  }

  // ---- Normalised options -----------------------------------------------------

  private get normalizedOptions(): SelectOption[] {
    return normalizeOptions(this.args.items ?? []);
  }

  private get filteredOptions(): SelectOption[] {
    const query = this._searchQuery.trim().toLowerCase();
    if (!query || this.hasRemoteSearch) return this.normalizedOptions;
    return this.normalizedOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(query) ||
        (o.description?.toLowerCase().includes(query) ?? false)
    );
  }

  private get groupedFilteredOptions(): { group: string | null; options: SelectOption[] }[] {
    return groupOptions(this.filteredOptions);
  }

  private get flatFilteredOptions(): SelectOption[] {
    return this.filteredOptions.filter((o) => !o.disabled);
  }

  // ---- Selection state -------------------------------------------------------

  private get selectedKeysSet(): Set<string> {
    if (this.multiple) {
      return new Set(this.args.selectedKeys ?? []);
    }
    if (this.args.selected != null && this.args.selected !== '') {
      return new Set([this.args.selected]);
    }
    return new Set();
  }

  private get selectedOptions(): SelectOption[] {
    const keys = this.selectedKeysSet;
    if (keys.size === 0) return [];

    // Try to resolve from current items first, fall back to cache
    const result: SelectOption[] = [];
    const currentMap = new Map(this.normalizedOptions.map((o) => [o.key, o]));

    for (const key of keys) {
      const opt = currentMap.get(key) ?? this._selectedCache.get(key);
      if (opt) result.push(opt);
    }
    return result;
  }

  private get displayText(): string {
    if (this.selectedOptions.length === 0) {
      return this.args.placeholder ?? 'Select…';
    }
    if (this.multiple) {
      return ''; // chips shown instead
    }
    return this.selectedOptions[0]?.label ?? '';
  }

  private get isPlaceholder(): boolean {
    return this.selectedOptions.length === 0;
  }

  private get showClearButton(): boolean {
    return this.clearable && this.selectedKeysSet.size > 0 && !this.args.disabled;
  }

  // ---- Dropdown positioning ---------------------------------------------------

  private get dropdownStyle() {
    if (!this._triggerRect) return htmlSafe('display:none;');

    const pos = computeDropdownPosition(this._triggerRect, {
      maxHeight: this.maxHeight,
      offset: 4,
    });

    const translateY = pos.openAbove ? 'translateY(-100%)' : 'none';

    // Use position:fixed so the dropdown is placed relative to the viewport.
    // This avoids scroll-offset bugs when the body has position:fixed (e.g.
    // inside a Drawer that locks body scroll), where window.scrollY is 0.
    return htmlSafe(
      `
      position: fixed;
      top: ${pos.top}px;
      left: ${pos.left}px;
      width: ${pos.width}px;
      max-height: ${this.maxHeight}px;
      transform-origin: ${pos.transformOrigin};
      transform: ${translateY};
      z-index: 90;
    `.replace(/\n\s*/g, ' ')
    );
  }

  // ---- Lifecycle modifier -----------------------------------------------------

  /**
   * Tracks the trigger element for positioning.
   */
  registerTrigger = modifier((element: HTMLElement) => {
    this._triggerElement = element;
    return () => {
      this._triggerElement = null;
    };
  });

  /**
   * Tracks the listbox element, installs outside-click and scroll-close listeners
   * using shared floating utilities.
   *
   * **Outside-click** uses a bubbling-phase `mousedown` listener on `document`.
   * Unlike a full-viewport backdrop `<div>`, this approach does **not** block
   * event propagation — so clicks that land on another element (e.g. the Drawer
   * overlay) still reach that element's own handlers.
   *
   * We use `mousedown` (not `click`) so the containment check runs before the
   * option's `click` handler.  When clicking an option, `mousedown` fires first;
   * the target is inside the listbox, so we bail.  Then `click` fires on the
   * option div, calling `selectOption`.  No race condition.
   *
   * **Scroll-close** uses a capture-phase `scroll` listener with 150 ms debounce.
   * When an ancestor outside the dropdown scrolls (e.g. a Drawer body), the
   * dropdown would visually detach from its trigger since we use position:fixed.
   * The debounce prevents tiny scroll micro-adjustments triggered by a click
   * from racing with the click handler.
   */
  registerListbox = modifier((element: HTMLElement) => {
    this._listboxElement = element;

    const cleanupOutsideClick = installOutsideClickHandler([this._triggerElement, element], () =>
      this.close()
    );

    const cleanupScroll = installScrollHandler({
      floatingEl: element,
      mode: 'close',
      onClose: () => this.close(),
      debounceMs: 150,
    });

    // Document-level keydown captures arrow keys even when focus drifts inside
    // the portal (e.g. to an option div), preventing page scroll.
    // Only fires when focus is NOT already on the trigger (or anything inside it)
    // or the search input, where element-level handlers already handle the event.
    const onKeyDown = (event: KeyboardEvent) => {
      if (!this._isOpen) return;
      const target = event.target as HTMLElement | null;
      if (
        target === this._searchInputElement ||
        (target && this._triggerElement?.contains(target))
      ) {
        return;
      }
      this.handleKeyDown(event);
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      cleanupOutsideClick();
      cleanupScroll();
      this._listboxElement = null;
    };
  });

  registerSearchInput = modifier((element: HTMLInputElement) => {
    this._searchInputElement = element;
    // Auto-focus the search input when the dropdown opens
    requestAnimationFrame(() => element.focus());
    return () => {
      this._searchInputElement = null;
    };
  });

  // ---- Event handlers ---------------------------------------------------------

  toggleOpen = () => {
    if (this.args.disabled) return;
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open = () => {
    if (this._isOpen || this.args.disabled) return;
    // Snapshot trigger position
    if (this._triggerElement) {
      this._triggerRect = this._triggerElement.getBoundingClientRect();
    }
    this._isOpen = true;
    this._highlightedIndex = -1;
    this._searchQuery = '';
  };

  close = () => {
    if (!this._isOpen) return;
    this._isOpen = false;
    this._searchQuery = '';
    this._highlightedIndex = -1;
    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
      this._searchTimer = undefined;
    }
  };

  handleSearchInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this._searchQuery = input.value;
    this._highlightedIndex = -1;

    // Debounced remote search callback
    if (this.hasRemoteSearch) {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.args.onSearch?.(this._searchQuery);
      }, this.searchDebounce);
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (!this._isOpen) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const opts = this.flatFilteredOptions;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._highlightedIndex = Math.min(this._highlightedIndex + 1, opts.length - 1);
        this.scrollHighlightedIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0);
        this.scrollHighlightedIntoView();
        break;
      case 'Enter':
        event.preventDefault();
        if (this._highlightedIndex >= 0 && opts[this._highlightedIndex]) {
          this.selectOption(opts[this._highlightedIndex]!);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this._triggerElement?.focus();
        break;
      case 'Tab':
        this.close();
        break;
    }
  };

  selectOption = (option: SelectOption) => {
    if (option.disabled) return;

    // Cache the selected option so remote-search Selects can display
    // the label even after @items changes to a different search result set.
    this._selectedCache.set(option.key, option);

    if (this.multiple) {
      const current = new Set(this.args.selectedKeys ?? []);
      if (current.has(option.key)) {
        current.delete(option.key);
      } else {
        current.add(option.key);
      }
      this.args.onChangeKeys?.([...current]);
      // In multi mode, keep dropdown open and refocus search
      this._searchQuery = '';
      requestAnimationFrame(() => this._searchInputElement?.focus());
    } else {
      this.args.onChange?.(option.key);
      this.close();
      this._triggerElement?.focus();
    }
  };

  removeChip = (key: string) => {
    const current = new Set(this.args.selectedKeys ?? []);
    current.delete(key);
    this._selectedCache.delete(key);
    this.args.onChangeKeys?.([...current]);
  };

  clearAll = (event: Event) => {
    event.stopPropagation();
    this._selectedCache.clear();
    if (this.multiple) {
      this.args.onChangeKeys?.([]);
    } else {
      this.args.onChange?.('');
    }
  };

  private scrollHighlightedIntoView() {
    requestAnimationFrame(() => {
      const highlighted = this._listboxElement?.querySelector('[data-highlighted="true"]');
      highlighted?.scrollIntoView({ block: 'nearest' });
    });
  }

  // ---- Helpers for template ---------------------------------------------------

  isSelected = (key: string): boolean => {
    return this.selectedKeysSet.has(key);
  };

  isHighlighted = (option: SelectOption): boolean => {
    if (this._highlightedIndex < 0) return false;
    const opts = this.flatFilteredOptions;
    const target = opts[this._highlightedIndex];
    return target !== undefined && target.key === option.key;
  };

  // ---- Template ---------------------------------------------------------------

  <template>
    <div data-test-select class="reimagined-select relative" ...attributes>
      {{! Visually-hidden select for FormData integration and focus/scroll support }}
      {{#if @name}}
        <select
          name={{@name}}
          class="sr-only"
          tabindex="-1"
          aria-labelledby={{if @label this.labelId}}
        >
          <option value={{this.formValue}} selected>{{this.formValue}}</option>
        </select>
      {{/if}}

      {{! Label }}
      {{#if @label}}
        <label
          data-test-select-label
          id={{this.labelId}}
          class="block text-sm font-medium text-gray-700 mb-1"
        >
          {{@label}}
        </label>
      {{/if}}

      {{! Trigger button }}
      <button
        data-test-select-trigger
        id={{this.triggerId}}
        type="button"
        role="combobox"
        aria-expanded="{{this._isOpen}}"
        aria-haspopup="listbox"
        aria-controls={{this.listboxId}}
        aria-activedescendant={{this.activeDescendantId}}
        aria-labelledby={{if @label this.labelId}}
        aria-invalid={{if this.hasErrors "true"}}
        class="app-input app-input--{{this.size}}
          flex items-center text-left cursor-default
          {{if this.hasErrors 'app-input--has-error'}}
          {{if this._isOpen 'app-input--is-active'}}"
        {{this.registerTrigger}}
        {{on "click" this.toggleOpen}}
        {{on "keydown" this.handleKeyDown}}
        disabled={{@disabled}}
      >
        {{#if (has-block "trigger")}}
          <div class="flex-1 min-w-0">
            {{yield
              (hash
                selectedOptions=this.selectedOptions
                isOpen=this._isOpen
                placeholder=(if @placeholder @placeholder "Select…")
              )
              to="trigger"
            }}
          </div>
        {{else}}
          {{! Start content slot (e.g. leading icon) }}
          {{#if (has-block "startContent")}}
            <span class="shrink-0 flex items-center text-gray-400 mr-2">
              {{yield to="startContent"}}
            </span>
          {{/if}}

          <div class="flex-1 min-w-0 flex flex-wrap items-center gap-1">
            {{#if this.multiple}}
              {{#each this.selectedOptions as |opt|}}
                <span
                  data-test-select-chip
                  class="tio-select-chip inline-flex items-center gap-1 max-w-full px-2 py-0.5 text-xs font-medium"
                >
                  <span class="truncate">{{opt.label}}</span>
                  {{#unless @disabled}}
                    {{! template-lint-disable no-nested-interactive }}
                    <button
                      data-test-select-chip-remove
                      type="button"
                      class="tio-select-chip__remove shrink-0 ml-0.5 p-0.5"
                      aria-label="Remove {{opt.label}}"
                      {{on "click" (fn this.removeChip opt.key)}}
                    >
                      <PhX @size={{12}} />
                    </button>
                  {{/unless}}
                </span>
              {{/each}}
              {{#if this.isPlaceholder}}
                <span class="app-input-placeholder truncate">
                  {{if @placeholder @placeholder "Select…"}}
                </span>
              {{/if}}
            {{else}}
              <span class="truncate {{if this.isPlaceholder 'app-input-placeholder'}}">
                {{this.displayText}}
              </span>
            {{/if}}
          </div>

          {{! End content slot (e.g. trailing icon or badge) }}
          {{#if (has-block "endContent")}}
            <span class="shrink-0 flex items-center text-gray-400">
              {{yield to="endContent"}}
            </span>
          {{/if}}
        {{/if}}

        {{! Right side controls }}
        <div class="flex items-center gap-1 ml-2 shrink-0">
          {{#if this.showClearButton}}
            {{! template-lint-disable no-nested-interactive }}
            <button
              data-test-select-clear
              type="button"
              class="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Clear selection"
              {{on "click" this.clearAll}}
            >
              <PhX @size={{14}} />
            </button>
          {{/if}}
          <PhCaretDown
            @size={{16}}
            class="text-gray-400 transition-transform duration-200 {{if this._isOpen 'rotate-180'}}"
          />
        </div>
      </button>

      {{! Hint text }}
      {{#if @hint}}
        <p data-test-select-hint class="mt-1 text-xs text-gray-500">
          {{@hint}}
        </p>
      {{/if}}

      {{! Error messages }}
      {{#if this.hasErrors}}
        <div data-test-select-errors class="mt-1 space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs text-red-600 dark:text-red-400">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}

      {{! Dropdown — portaled to body }}
      {{#if this._isOpen}}
        {{#in-element this.portalDestination insertBefore=null}}
          <div
            data-test-select-dropdown
            data-floating-trigger-id={{this.triggerId}}
            class="app-popover animate-in fade-in slide-in-from-top-1 duration-150"
            style={{this.dropdownStyle}}
            {{this.registerListbox}}
          >
            {{! Search input }}
            {{#if this.searchable}}
              <div class="app-popover-section app-popover-section--bordered-bottom">
                <div class="relative">
                  <PhMagnifyingGlass
                    @size={{16}}
                    class="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"
                  />
                  {{! template-lint-disable require-input-label }}
                  <input
                    data-test-select-search
                    type="text"
                    aria-label="Search options"
                    class="app-input app-input--sm pl-8 {{if @isLoading 'pr-8'}}"
                    placeholder="Search…"
                    autocomplete="off"
                    value={{this._searchQuery}}
                    {{this.registerSearchInput}}
                    {{on "input" this.handleSearchInput}}
                    {{on "keydown" this.handleKeyDown}}
                    aria-activedescendant={{this.activeDescendantId}}
                  />
                  {{#if @isLoading}}
                    <div class="absolute right-2.5 top-1/2 -translate-y-1/2">
                      <PhSpinner @size={{16}} class="text-[var(--brand)] animate-spin" />
                    </div>
                  {{/if}}
                </div>
              </div>
            {{/if}}

            {{! Header slot }}
            {{#if (has-block "header")}}
              <div
                data-test-select-header
                class="app-popover-section app-popover-section--bordered-bottom"
              >
                {{yield to="header"}}
              </div>
            {{/if}}

            {{! Options list }}
            <div
              data-test-select-options
              class="app-popover-body"
              role="listbox"
              id={{this.listboxId}}
              aria-multiselectable="{{this.multiple}}"
              aria-labelledby={{if @label this.labelId}}
            >
              {{#if @isLoading}}
                <div class="app-popover-loading">
                  <PhSpinner @size={{20}} class="text-[var(--brand)] animate-spin" />
                  <span>Loading…</span>
                </div>
              {{else if (eq this.filteredOptions.length 0)}}
                <div data-test-select-empty class="app-popover-empty">
                  {{this.noResultsText}}
                </div>
              {{else}}
                {{#each this.groupedFilteredOptions as |group|}}
                  {{#if group.group}}
                    <div
                      data-test-select-group-label
                      role="presentation"
                      class="app-popover-group-label"
                    >
                      {{group.group}}
                    </div>
                  {{/if}}
                  {{#each group.options as |option|}}
                    {{! template-lint-disable no-nested-interactive require-presentational-children }}
                    <div
                      data-test-select-option
                      id={{this.optionId option.key}}
                      role="option"
                      aria-selected="{{this.isSelected option.key}}"
                      data-highlighted="{{this.isHighlighted option}}"
                      class="app-popover-option app-popover-option--{{this.size}}
                        {{if option.disabled 'app-popover-option--disabled'}}
                        {{if (this.isHighlighted option) 'app-popover-option--highlighted'}}
                        {{if (this.isSelected option.key) 'app-popover-option--selected'}}"
                      tabindex="-1"
                      aria-disabled={{if option.disabled "true"}}
                      {{on "click" (fn this.selectOption option)}}
                    >
                      {{! Checkbox indicator for multi-select }}
                      {{#if this.multiple}}
                        <span
                          aria-hidden="true"
                          class="app-popover-check
                            {{if (this.isSelected option.key) 'app-popover-check--checked'}}"
                        >
                          {{#if (this.isSelected option.key)}}
                            <PhCheck @size={{10}} />
                          {{/if}}
                        </span>
                      {{/if}}

                      {{! Option icon }}
                      {{#if option.icon}}
                        <span aria-hidden="true" class="shrink-0">
                          {{#let option.icon as |Icon|}}
                            <Icon @size={{16}} class="text-[var(--text-tertiary)]" />
                          {{/let}}
                        </span>
                      {{/if}}

                      {{! Option content }}
                      {{#if (has-block "option")}}
                        <div class="flex-1 min-w-0">
                          {{yield option to="option"}}
                        </div>
                      {{else}}
                        <div class="flex-1 min-w-0">
                          <span class="block truncate">{{option.label}}</span>
                          {{#if option.description}}
                            <span
                              class="block text-xs text-[var(--text-tertiary)] whitespace-normal break-words"
                            >
                              {{option.description}}
                            </span>
                          {{/if}}
                        </div>
                      {{/if}}

                      {{! Single-select check mark }}
                      {{#unless this.multiple}}
                        {{#if (this.isSelected option.key)}}
                          <span aria-hidden="true" class="shrink-0">
                            <PhCheck @size={{16}} class="text-[var(--brand)]" />
                          </span>
                        {{/if}}
                      {{/unless}}
                    </div>
                  {{/each}}
                {{/each}}
              {{/if}}
            </div>

            {{! Footer slot }}
            {{#if (has-block "footer")}}
              <div
                data-test-select-footer
                class="app-popover-section app-popover-section--bordered-top"
              >
                {{yield to="footer"}}
              </div>
            {{/if}}
          </div>
        {{/in-element}}
      {{/if}}
    </div>
  </template>
}

export type { SelectSignature, SelectSize };
